// src/services/productsService.js
/**
 * Products Service - Handles product-related API communication with backend
 * Backend API Base URL: Production VPS via config.js
 */

import axios from 'axios';
import { NODE_SUPPLIERS_API_URL } from '../config';

const PRODUCTS_API_URL = `${NODE_SUPPLIERS_API_URL}/orders`;

/**
 * Search products filtered by supplier's related brands and categories
 * @param {number} supplierId - Supplier ID to filter products by
 * @param {string} searchTerm - Search text (minimum 2 characters)
 * @param {number} maxResults - Maximum results to return (default: 100, max: 500)
 * @returns {Promise} Response with products array and search info
 */
export const searchProductsBySupplier = async (supplierId, searchTerm, maxResults = 100) => {
    try {
        const token = localStorage.getItem('token');

        const response = await axios.post(
            `${PRODUCTS_API_URL}/supplier/${supplierId}/search-products`,
            {
                searchTerm,
                maxResults
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error searching products by supplier:', error);

        if (error.response) {
            if (error.response.status === 404) {
                throw new Error(error.response.data.message || 'Supplier not found or has no configuration');
            }
            if (error.response.status === 400) {
                throw new Error(error.response.data.message || 'Invalid search parameters');
            }
            throw new Error(error.response.data.message || 'Error searching products');
        }

        throw error;
    }
};

/**
 * Check for duplicate products across different brands
 * @param {string} productDescription - Product description to check (optional)
 * @param {string} productCode - Product code to check (optional)
 * @param {number} excludeProductId - Product ID to exclude from results (optional)
 * @returns {Promise} Response with duplicate check results
 */
export const checkProductDuplicates = async (productDescription = null, productCode = null, excludeProductId = null) => {
    try {
        // Validate at least one parameter
        if (!productDescription && !productCode) {
            throw new Error('At least one of productDescription or productCode is required');
        }

        const token = localStorage.getItem('token');

        const requestBody = {};
        if (productDescription) requestBody.productDescription = productDescription;
        if (productCode) requestBody.productCode = productCode;
        if (excludeProductId) requestBody.excludeProductId = excludeProductId;

        const response = await axios.post(
            `${NODE_SUPPLIERS_API_URL}/products/check-duplicates`,
            requestBody,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error checking product duplicates:', error);

        if (error.response) {
            if (error.response.status === 400) {
                throw new Error(error.response.data.message || 'Invalid check parameters');
            }
            throw new Error(error.response.data.message || 'Error checking duplicates');
        }

        throw error;
    }
};
